// One list of what a warehouse is made of, used by three different things:
// the screen that edits one, the template you download, and the file you
// import back. Keeping them on the same list is the whole point - add a field
// here and the template grows a column, the importer learns to read it, and
// nothing drifts out of step.
//
//   column   the heading in the CSV file
//   key      the column in the database
//   kind     text | yesno | region | referral | person | warehouse
//   example  shown in the template's example row, so the format is obvious
//   alias    other headings that mean the same thing. These are the column
//            names Claimsoft's own export uses, so a file straight out of
//            Claimsoft imports without anyone renaming a single column.

export const WAREHOUSE_FIELDS = [
  { column: 'Warehouse Name',       key: 'name', alias: ['Warehouse_Name'],               kind: 'text',      required: true,
    example: 'SAT-MAIN', help: 'Exactly as it is in Claimsoft, prefix and all.' },
  { column: 'Warehouse Number',     key: 'warehouse_number', alias: ['Warehouse_Number'],   kind: 'text',      match: true,
    example: 'SAT-001', help: 'What a row is matched on. Blank means match on name instead.' },
  { column: 'Region',               key: 'region_id', alias: ['Assigned_Billing_Location', 'Assigned Billing Location'],          kind: 'region',
    example: 'San Antonio', help: 'Left blank, this is worked out from the name prefix.' },
  { column: 'Referral Source',      key: 'referral_source_id', alias: ['Referral_Source'], kind: 'referral',
    example: 'SAT-MAIN', help: 'The referral source this warehouse pairs with, one for one.' },
  { column: 'Warehouse Manager',    key: 'manager_id', alias: ['WarehouseManagerName', 'Warehouse_Manager'],         kind: 'person',
    example: 'Jake Ham', help: 'Matched to a person by name, or by email if you put one.' },
  { column: 'Primary Warehouse',          key: 'primary_warehouse_id', alias: ['PrimaryWarehouse', 'Primary_Warehouse'], kind: 'warehouse',
    example: 'AUSTIN NORTH', help: 'The main warehouse this one belongs under.' },
  { column: 'Ship To Address',      key: 'ship_to', alias: ['ShipTo'],            kind: 'yesno',     example: 'Yes' },
  { column: 'Transfer Only',        key: 'transfer_only', alias: ['TransferOnly'],      kind: 'yesno',     example: 'No' },
  { column: 'Inactive',             key: 'is_inactive', alias: ['Inactive'],        kind: 'yesno',     example: 'No' },
  { column: 'Address Line 1',       key: 'address1', alias: ['Address1'],           kind: 'text',      example: '8900 Shoal Creek Blvd' },
  { column: 'Address Line 2',       key: 'address2', alias: ['Address2'],           kind: 'text',      example: 'Ste 200' },
  { column: 'City',                 key: 'city',               kind: 'text',      example: 'Austin' },
  { column: 'State',                key: 'state',              kind: 'text',      example: 'TX' },
  { column: 'ZIP',                  key: 'zip', alias: ['Zip'],                kind: 'text',      example: '78757-6818' },
  { column: 'Country',              key: 'country',            kind: 'text',      example: 'United States' },
  { column: 'Phone',                key: 'phone', alias: ['Phone1'],              kind: 'text',      example: '5123711700' },
  { column: 'Phone Ext',            key: 'phone_ext', alias: ['Phone1Ext'],          kind: 'text',      example: '' },
  { column: 'Phone 2',              key: 'phone2', alias: ['Phone2'],             kind: 'text',      example: '' },
  { column: 'Phone 2 Ext',          key: 'phone2_ext', alias: ['Phone2Ext'],         kind: 'text',      example: '' },
  { column: 'Notes',                key: 'notes', alias: ['Notes'],              kind: 'text',      example: '' },
  { column: 'Assigned Users',       key: 'assigned_users',     kind: 'people',
    example: 'first.last@company.com; other.person@company.com',
    help: 'Emails separated by a semicolon. These people get access to this warehouse.' },
];

// Columns that hold a link to something else rather than plain text. These are
// the ones the import has to look up, and the ones worth reporting on when a
// lookup fails.
export const LINK_KINDS = ['region', 'referral', 'person', 'warehouse', 'people'];

export const yesNo = v => (v ? 'Yes' : 'No');

// Accepts anything a person or a spreadsheet is likely to put in a yes/no cell.
export function readYesNo(raw) {
  const s = String(raw ?? '').trim().toLowerCase();
  if (!s) return null;
  if (['y', 'yes', 'true', '1', 'x', 't'].includes(s)) return true;
  if (['n', 'no', 'false', '0', 'f'].includes(s)) return false;
  return null;
}

// Claimsoft names are not tidy: "ZJake Ham", "RICK SMITH", "John Rogers -
// Austin". Flattened the same way the database does it, so both sides agree.
export function flattenPersonName(n) {
  return String(n ?? '')
    .trim().toUpperCase()
    .replace(/^Z(?=[A-Z])/, '')
    .replace(/\s+-\s+.*$/, '')
    .replace(/\s+/g, ' ')
    .trim();
}

export const flatten = s => String(s ?? '').trim().toUpperCase().replace(/\s+/g, ' ');


// Every heading that maps to a field, pointed at the field. Built once from the
// list above so the template, the screen and the importer cannot disagree.
export const FIELD_BY_HEADING = (() => {
  const out = {};
  WAREHOUSE_FIELDS.forEach(f => {
    out[f.column.trim().toLowerCase()] = f;
    (f.alias || []).forEach(a => { out[a.trim().toLowerCase()] = f; });
  });
  return out;
})();
